import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { FaLandmark, FaPlus, FaTrash, FaTimes, FaEnvelope, FaPhone } from "react-icons/fa";

const D2Lenders = () => {
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    type: "Major Bank",
    contactEmail: "",
    phone: "",
    isActive: true,
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  const fetchLenders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-lenders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        setLenders(data.data || []);
      }
    } catch (err) {
      console.error("Failed to fetch lenders", err);
      toast.error("Failed to load lenders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLenders();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ 
      ...prev, 
      [name]: type === "checkbox" ? checked : value 
    }));
  };

  const handleSaveLender = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-lenders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Lender added successfully!");
        setShowModal(false);
        setFormData({ name: "", type: "Major Bank", contactEmail: "", phone: "", isActive: true });
        fetchLenders();
      } else {
        toast.error(data.message || "Failed to add lender");
      }
    } catch (err) {
      console.error("Error saving lender", err);
      toast.error("Failed to add lender");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lender?")) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${getBackendBaseUrl()}/d2-lenders/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Lender deleted");
        fetchLenders();
      }
    } catch (err) {
      toast.error("Failed to delete lender");
    }
  };

  return (
    <div className="p-8 h-full w-full bg-slate-50/50 flex flex-col overflow-y-auto animate-fade-in-up">
      {/* Title Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight flex items-center gap-3">
            <span className="p-2.5 bg-indigo-50 text-indigo-600 rounded-2xl shadow-sm border border-indigo-100">
              <FaLandmark size={24} />
            </span>
            Lenders Management
          </h1>
          <p className="text-slate-500 text-xs mt-1 leading-relaxed">
            Manage your partner automotive lenders and financing institutional profiles.
          </p>
        </div>
        {user?.role !== "Salesperson" && (
          <button
            onClick={() => setShowModal(true)}
            className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-5 py-2.5 rounded-xl font-semibold hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 shadow-md transition-all duration-200 flex items-center gap-2 text-sm cursor-pointer"
          >
            <FaPlus size={12} /> Add Lender
          </button>
        )}
      </div>

      {/* Lenders Table Container */}
      <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200/80 text-slate-500 text-xs font-bold uppercase tracking-wider">
                <th className="px-6 py-4">Lender Name</th>
                <th className="px-6 py-4">Institution Type</th>
                <th className="px-6 py-4">Contact Email</th>
                <th className="px-6 py-4">Phone</th>
                <th className="px-6 py-4">Status</th>
                {user?.role !== "Salesperson" && <th className="px-6 py-4 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={user?.role === "Salesperson" ? "5" : "6"} className="px-6 py-10 text-center text-slate-400 text-sm">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <span className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></span>
                      Loading lenders...
                    </div>
                  </td>
                </tr>
              ) : lenders.length === 0 ? (
                <tr>
                  <td colSpan={user?.role === "Salesperson" ? "5" : "6"} className="px-6 py-12 text-center text-slate-400 text-sm italic">
                    No lenders found. Click "Add Lender" to set up a financial partner.
                  </td>
                </tr>
              ) : (
                lenders.map((lender) => (
                  <tr key={lender._id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 font-bold text-slate-800 text-sm">{lender.name}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="px-2.5 py-1 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-lg text-xs font-semibold">
                        {lender.type || "Major Bank"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-medium">
                      {lender.contactEmail ? (
                        <span className="flex items-center gap-1.5"><FaEnvelope className="text-slate-400" size={12} /> {lender.contactEmail}</span>
                      ) : "dealer.desk@" + lender.name.toLowerCase().replace(/\s+/g, '') + ".com"}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600">
                      {lender.phone ? (
                        <span className="flex items-center gap-1.5"><FaPhone className="text-slate-400" size={11} /> {lender.phone}</span>
                      ) : "(800) 555-0100"}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      {lender.isActive ?? true ? (
                        <span className="px-2.5 py-1 bg-emerald-50 text-emerald-700 border border-emerald-100/50 rounded-lg text-xs font-semibold">Active</span>
                      ) : (
                        <span className="px-2.5 py-1 bg-red-50 text-red-700 border border-red-100/50 rounded-lg text-xs font-semibold">Inactive</span>
                      )}
                    </td>
                    {user?.role !== "Salesperson" && (
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleDelete(lender._id)}
                          className="text-red-500 hover:text-red-700 font-semibold text-xs tracking-wide bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg transition-colors border border-red-200/30 flex items-center gap-1.5 ml-auto cursor-pointer"
                        >
                          <FaTrash size={10} /> Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* True Fullscreen Modal using ReactDOM.createPortal */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-900/70 backdrop-blur-sm p-4 animate-fade-in-up">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-2xl p-8 w-full max-w-lg relative">
            <button 
              onClick={() => setShowModal(false)}
              className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 p-2 hover:bg-slate-100 rounded-full transition-colors cursor-pointer"
            >
              <FaTimes size={16} />
            </button>
            
            <h2 className="text-2xl font-bold text-slate-800 tracking-tight mb-1">Add Lender</h2>
            <p className="text-slate-400 text-xs mb-6 border-b pb-4">
              Register a new financial partner profile in the system.
            </p>
            
            <form onSubmit={handleSaveLender} className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Lender Name *</label>
                <input required type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 outline-none transition-all" placeholder="e.g. Bank of America Auto" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Institution Type</label>
                  <select name="type" value={formData.type} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white focus:border-indigo-600 outline-none">
                    <option value="Captive">Captive (OEM)</option>
                    <option value="Major Bank">Major Bank</option>
                    <option value="Credit Union">Credit Union</option>
                    <option value="Subprime Lender">Subprime Lender</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Phone Number</label>
                  <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none" placeholder="(800) 215-6195" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-600 uppercase mb-1">Contact Email</label>
                <input type="email" name="contactEmail" value={formData.contactEmail} onChange={handleInputChange} className="w-full border border-slate-200 rounded-xl p-3 text-sm bg-slate-50/50 focus:bg-white outline-none" placeholder="dealerdesk@bankofamerica.com" />
              </div>

              <div className="flex justify-end gap-3 mt-6 border-t pt-5">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-5 py-2.5 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 font-semibold text-sm transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl text-white font-semibold hover:shadow-lg shadow-md transition-all text-sm cursor-pointer"
                >
                  Save Lender
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default D2Lenders;
